import React, { useState, useEffect } from 'react';
import {
  Users,
  Shield,
  CheckCircle2,
  Database,
  Scale,
  FileText,
  Eye,
  User,
  Plus,
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  Clock,
  AlertCircle,
  RefreshCw,
  Edit3,
  Settings,
  Lock,
  Unlock,
  Info,
  X
} from 'lucide-react';
import { apiFetch } from '../services/api_service';

const API = process.env.REACT_APP_BACKEND_URL;

// Role icon mapping
const ROLE_ICONS = {
  admin: Shield,
  compliance_manager: CheckCircle2,
  data_analyst: Database,
  legal_counsel: Scale,
  policy_adviser: FileText,
  auditor: Eye,
  viewer: User
};

// Role color classes
const ROLE_COLORS = {
  red: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-500' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-500' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-500' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-500' },
  teal: { bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-500' },
  slate: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-500' },
  gray: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-500' }
};

const TeamManagement = () => {
  const [myRole, setMyRole] = useState(null);
  const [roles, setRoles] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRoleFilter, setSelectedRoleFilter] = useState('all');
  const [expandedMember, setExpandedMember] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [assignReason, setAssignReason] = useState('');

  // Fetch my role and available roles
  useEffect(() => {
    const fetchRoleData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        // Fetch my role
        const myRoleRes = await fetch(`${API}/api/rbac/my-role`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (myRoleRes.ok) {
          const data = await myRoleRes.json();
          if (data.success) setMyRole(data);
        }
        
        // Fetch all roles
        const rolesRes = await fetch(`${API}/api/rbac/roles`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (rolesRes.ok) {
          const data = await rolesRes.json();
          if (data.success) setRoles(data.roles || []);
        }
        
        // Fetch team members
        const teamRes = await fetch(`${API}/api/rbac/team`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (teamRes.ok) {
          const data = await teamRes.json();
          if (data.success) {
            setTeamMembers(data.members || []);
          } else if (data.error?.includes('Permission denied')) {
            // User doesn't have permission to view team, just show their own info
            setTeamMembers([]);
          }
        }
      } catch (err) {
        console.error('Error fetching role data:', err);
        setError('Failed to load role data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchRoleData();
  }, []);

  // Assign role to user
  const handleAssignRole = async () => {
    if (!selectedMember || !newRole) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/rbac/assign-role`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_email: selectedMember.email,
          role: newRole,
          reason: assignReason
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Update team members list
          setTeamMembers(prev => prev.map(m => 
            m.email === selectedMember.email 
              ? { ...m, role: newRole, role_label: roles.find(r => r.value === newRole)?.label || newRole }
              : m
          ));
          setShowAssignModal(false);
          setSelectedMember(null);
          setNewRole('');
          setAssignReason('');
        }
      }
    } catch (err) {
      console.error('Error assigning role:', err);
    }
  };

  // Filter team members
  const filteredMembers = teamMembers.filter(member => {
    if (selectedRoleFilter !== 'all' && member.role !== selectedRoleFilter) return false;
    if (searchTerm && !member.email?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !member.full_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  // Check if user can manage roles
  const canManageRoles = myRole?.permissions?.includes('roles:assign');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="team-loading">
        <RefreshCw className="animate-spin text-purple-600" size={32} />
        <span className="ml-3 text-slate-600">Loading team data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-management">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-700 rounded-xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-xl">
              <Users size={32} />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Team & Roles</h1>
              <p className="text-purple-100">Manage team members and their permissions</p>
            </div>
          </div>
          {canManageRoles && (
            <button 
              onClick={() => { setSelectedMember({ email: '', full_name: 'New User' }); setShowAssignModal(true); }}
              className="px-4 py-2 bg-white text-purple-700 rounded-lg font-semibold hover:bg-purple-50 flex items-center gap-2"
              data-testid="invite-user-btn"
            >
              <Plus size={18} />
              Invite User
            </button>
          )}
        </div>
      </div>

      {/* My Role Card */}
      {myRole && (
        <div className="bg-white rounded-xl shadow p-6" data-testid="my-role-card">
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <User size={20} className="text-purple-600" />
            My Access Level
          </h2>
          <div className="flex items-start gap-6">
            <div className={`p-4 rounded-xl ${ROLE_COLORS[myRole.role_color]?.bg || 'bg-gray-100'}`}>
              {React.createElement(ROLE_ICONS[myRole.role] || User, { 
                size: 32, 
                className: ROLE_COLORS[myRole.role_color]?.text || 'text-gray-700' 
              })}
            </div>
            <div className="flex-1">
              <h3 className={`text-xl font-bold ${ROLE_COLORS[myRole.role_color]?.text || 'text-gray-700'}`}>
                {myRole.role_label}
              </h3>
              <p className="text-slate-600 mt-1">{myRole.role_description}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {myRole.permissions?.slice(0, 8).map(perm => (
                  <span key={perm} className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs">
                    {perm}
                  </span>
                ))}
                {myRole.permission_count > 8 && (
                  <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs">
                    +{myRole.permission_count - 8} more
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Role Overview */}
      <div className="bg-white rounded-xl shadow overflow-hidden" data-testid="roles-overview">
        <div className="p-4 border-b border-slate-200">
          <h2 className="font-bold text-slate-900 flex items-center gap-2">
            <Shield size={18} className="text-purple-600" />
            Available Roles ({roles.length})
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4">
          {roles.map(role => {
            const Icon = ROLE_ICONS[role.value] || User;
            const colorClasses = ROLE_COLORS[role.color] || ROLE_COLORS.gray;
            return (
              <div 
                key={role.value} 
                className={`p-4 rounded-lg border-l-4 ${colorClasses.border} bg-slate-50 hover:bg-slate-100 transition-colors`}
                data-testid={`role-card-${role.value}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={18} className={colorClasses.text} />
                  <span className={`font-semibold ${colorClasses.text}`}>{role.label}</span>
                </div>
                <p className="text-xs text-slate-600 mb-2">{role.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">{role.permission_count} permissions</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${colorClasses.bg} ${colorClasses.text}`}>
                    Level {role.level}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Team Members */}
      {(teamMembers.length > 0 || canManageRoles) && (
        <div className="bg-white rounded-xl shadow overflow-hidden" data-testid="team-members">
          <div className="p-4 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-slate-900 flex items-center gap-2">
                <Users size={18} className="text-purple-600" />
                Team Members ({filteredMembers.length})
              </h2>
              <div className="flex items-center gap-3">
                <select
                  value={selectedRoleFilter}
                  onChange={(e) => setSelectedRoleFilter(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  data-testid="role-filter"
                >
                  <option value="all">All Roles</option>
                  {roles.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm w-48"
                    data-testid="member-search"
                  />
                </div>
              </div>
            </div>
          </div>
          
          {filteredMembers.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {filteredMembers.map(member => {
                const Icon = ROLE_ICONS[member.role] || User;
                const colorClasses = ROLE_COLORS[member.role_color] || ROLE_COLORS.gray;
                const isExpanded = expandedMember === member.email;
                
                return (
                  <div key={member.email} data-testid={`member-${member.email}`}>
                    <div 
                      className="p-4 hover:bg-slate-50 cursor-pointer"
                      onClick={() => setExpandedMember(isExpanded ? null : member.email)}
                    >
                      <div className="flex items-center gap-4">
                        <button className="text-slate-400">
                          {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                        </button>
                        
                        <div className={`w-10 h-10 rounded-full ${colorClasses.bg} flex items-center justify-center`}>
                          <Icon size={20} className={colorClasses.text} />
                        </div>
                        
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">{member.full_name || member.email}</p>
                          <p className="text-sm text-slate-500">{member.email}</p>
                        </div>
                        
                        <div className={`px-3 py-1 rounded-full ${colorClasses.bg} ${colorClasses.text}`}>
                          <span className="text-sm font-medium">{member.role_label}</span>
                        </div>
                        
                        {canManageRoles && (
                          <button 
                            onClick={(e) => { e.stopPropagation(); setSelectedMember(member); setNewRole(member.role); setShowAssignModal(true); }}
                            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                            title="Change Role"
                          >
                            <Edit3 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className="px-4 pb-4 ml-14">
                        <div className="bg-slate-50 rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-slate-700 mb-2">Permissions</h4>
                          <div className="flex flex-wrap gap-2">
                            {member.permissions?.map(perm => (
                              <span key={perm} className="px-2 py-1 bg-white border border-slate-200 text-slate-600 rounded text-xs">
                                {perm}
                              </span>
                            ))}
                          </div>
                          <div className="mt-3 text-xs text-slate-500">
                            Assigned: {new Date(member.assigned_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-12 text-center">
              <Users size={48} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No Team Members</h3>
              <p className="text-slate-500">
                {canManageRoles 
                  ? 'Invite team members to get started.' 
                  : 'You do not have permission to view team members.'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Role Assignment Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="assign-role-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-bold text-slate-900">Assign Role</h3>
              <button onClick={() => setShowAssignModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">User</label>
                <p className="text-slate-900">{selectedMember?.email || 'New User'}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  data-testid="role-select"
                >
                  <option value="">Select a role...</option>
                  {roles.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Reason (optional)</label>
                <textarea
                  value={assignReason}
                  onChange={(e) => setAssignReason(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  rows={2}
                  placeholder="Why is this role being assigned?"
                />
              </div>
              
              {newRole && (
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-sm text-slate-600">
                    <strong>{roles.find(r => r.value === newRole)?.label}</strong>: {roles.find(r => r.value === newRole)?.description}
                  </p>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-3">
              <button 
                onClick={() => setShowAssignModal(false)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button 
                onClick={handleAssignRole}
                disabled={!newRole}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                data-testid="confirm-assign-btn"
              >
                Assign Role
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permission Legend */}
      <div className="bg-slate-900 rounded-xl p-6 text-white">
        <h3 className="text-lg font-bold mb-4">Permission Categories</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
          {[
            { name: 'Data', icon: Database, desc: 'Read, create, edit, delete data' },
            { name: 'Claims', icon: Shield, desc: 'Create, approve, publish claims' },
            { name: 'Reports', icon: FileText, desc: 'Generate and export reports' },
            { name: 'Users', icon: Users, desc: 'View and manage team' },
            { name: 'Audit', icon: Eye, desc: 'Read audit trail, add comments' }
          ].map(cat => (
            <div key={cat.name} className="bg-white/10 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <cat.icon size={16} />
                <span className="font-medium">{cat.name}</span>
              </div>
              <p className="text-xs text-slate-400">{cat.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TeamManagement;
